<template>
  <v-container fluid class="pa-6">
    <h1 class="text-h4 font-weight-bold mb-6">Расходы LLM</h1>

    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-4">{{ error }}</v-alert>

    <!-- The balance at the provider: its fall over a day is the real spend -->
    <v-card elevation="2" rounded="lg" class="mb-6">
      <v-card-title class="text-h6 font-weight-bold">Остаток на счёте</v-card-title>
      <v-card-text>
        <div v-if="!balances.length && !isLoading" class="text-disabled">
          Остаток ещё не записан: он проверяется раз в час.
        </div>
        <v-row v-for="b in balances" :key="b.provider" dense>
          <v-col cols="12" md="3"><div class="text-caption text-disabled">{{ b.provider }}, остаток</div><div class="text-h5">{{ formatMoney(b.balance, 2) }} $</div></v-col>
          <v-col cols="12" md="3"><div class="text-caption text-disabled">Убыль за 24 ч</div><div class="text-h5">{{ b.spent_24h === null ? '—' : formatMoney(b.spent_24h, 2) + ' $' }}</div></v-col>
          <v-col cols="12" md="3"><div class="text-caption text-disabled">Наш расчёт за 24 ч</div><div class="text-h5">{{ formatMoney(b.counted_24h, 2) }} $</div></v-col>
          <v-col cols="12" md="3"><div class="text-caption text-disabled">Проверено</div><div class="text-body-1">{{ formatTime(b.checked_at) }}</div></v-col>
        </v-row>
        <div class="text-caption text-disabled mt-2">
          Убыль остатка — настоящий расход; пополнение в эти сутки её уменьшает. Наш расчёт идёт по токенам и
          ценам ниже; учёт кэша и пика — с 18.10.2026, раньше расход завышен.
        </div>
      </v-card-text>
    </v-card>

    <!-- Spend by day; a day opens its tasks -->
    <v-card elevation="2" rounded="lg" class="mb-6">
      <v-card-title class="d-flex align-center">
        <span class="text-h6 font-weight-bold">Расход по дням (UTC)</span>
        <v-spacer></v-spacer>
        <v-btn-toggle v-model="days" density="compact" mandatory variant="outlined" @update:model-value="loadDaily">
          <v-btn :value="7">7 дней</v-btn>
          <v-btn :value="30">30 дней</v-btn>
          <v-btn :value="90">90 дней</v-btn>
        </v-btn-toggle>
      </v-card-title>
      <v-card-text>
        <v-table density="compact">
          <thead>
            <tr>
              <th>День</th>
              <th class="text-right">Вызовов</th>
              <th class="text-right">Входных токенов</th>
              <th class="text-right">Из кэша</th>
              <th class="text-right">Выходных токенов</th>
              <th class="text-right">Расход, $</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="d in byDay" :key="d.day">
              <tr class="cursor-pointer" @click="toggle(d.day)">
                <td><v-icon size="small">{{ open[d.day] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon> {{ formatDay(d.day) }}</td>
                <td class="text-right">{{ formatNumber(d.calls) }}</td>
                <td class="text-right">{{ formatNumber(d.prompt_tokens) }}</td>
                <td class="text-right">{{ share(d.cache_hit_tokens, d.prompt_tokens) }}</td>
                <td class="text-right">{{ formatNumber(d.completion_tokens) }}</td>
                <td class="text-right font-weight-bold">{{ formatMoney(d.cost_usd, 2) }}<span v-if="d.unpriced" class="text-warning" title="есть вызовы без цены"> *</span></td>
              </tr>
              <template v-if="open[d.day]">
                <tr v-for="r in d.rows" :key="d.day + r.feature + r.model" class="text-medium-emphasis">
                  <td class="pl-10">{{ featureLabel(r.feature) }} · {{ r.model }}</td>
                  <td class="text-right">{{ formatNumber(r.calls) }}</td>
                  <td class="text-right">{{ formatNumber(r.prompt_tokens) }}</td>
                  <td class="text-right">{{ share(r.cache_hit_tokens, r.prompt_tokens) }}</td>
                  <td class="text-right">{{ formatNumber(r.completion_tokens) }}</td>
                  <td class="text-right">{{ r.cost_usd === null ? 'нет цены' : formatMoney(r.cost_usd, 3) }}</td>
                </tr>
              </template>
            </template>
            <tr v-if="!byDay.length && !isLoading">
              <td colspan="6" class="text-center text-disabled">Расходов нет</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>

    <v-card elevation="2" rounded="lg">
      <v-card-title class="text-h6 font-weight-bold">Цены моделей</v-card-title>
      <v-card-text>
        <LlmPricesPanel @saved="loadAll" />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient'
import LlmPricesPanel from '@/components/admin/LlmPricesPanel.vue'
import { featureLabel, formatMoney, formatNumber } from '@/utils/usageStats'

export default {
  name: 'LlmCosts',
  components: { LlmPricesPanel },
  data() {
    return {
      balances: [],
      daily: [],
      days: 30,
      open: {},
      isLoading: false,
      error: ''
    }
  },
  computed: {
    // days newest first, each with its rows by task and model
    byDay() {
      const days = new Map()
      for (const r of this.daily) {
        if (!days.has(r.day)) {
          days.set(r.day, { day: r.day, calls: 0, prompt_tokens: 0, cache_hit_tokens: 0, completion_tokens: 0,
            cost_usd: 0, unpriced: false, rows: [] })
        }
        const d = days.get(r.day)
        d.calls += r.calls
        d.prompt_tokens += r.prompt_tokens
        d.cache_hit_tokens += r.cache_hit_tokens
        d.completion_tokens += r.completion_tokens
        if (r.cost_usd === null) d.unpriced = true
        else d.cost_usd += r.cost_usd
        d.rows.push(r)
      }
      return [...days.values()]
    }
  },
  mounted() {
    this.loadAll()
  },
  methods: {
    featureLabel,
    formatMoney,
    formatNumber,
    formatDay(value) {
      return new Date(value).toLocaleDateString('ru-RU')
    },
    formatTime(value) {
      return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
    },
    share(part, total) {
      return total ? `${Math.round((100 * part) / total)} %` : '—'
    },
    toggle(day) {
      this.open = { ...this.open, [day]: !this.open[day] }
    },
    async loadDaily() {
      try {
        const resp = await apiClient.get('/admin/usage/llm-daily', { params: { days: this.days } })
        this.daily = resp.data
      } catch (e) {
        this.error = 'Ошибка загрузки расходов: ' + (e.response?.data?.detail || e.message)
      }
    },
    async loadAll() {
      this.isLoading = true
      this.error = ''
      try {
        const resp = await apiClient.get('/admin/usage/balances')
        this.balances = resp.data
      } catch (e) {
        this.error = 'Ошибка загрузки остатка: ' + (e.response?.data?.detail || e.message)
      }
      await this.loadDaily()
      this.isLoading = false
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
